import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import TrackRow from "@/components/music/TrackRow";
import { Loader2, Play, Disc3, DownloadCloud } from "lucide-react";

export default function AlbumDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const { playQueue } = useMusicPlayer();
  const { t } = useI18n();
  const { user } = useAuth();
  const [completing, setCompleting] = useState(false);
  const isAdmin = user?.role === "admin";

  // An album usually arrives with only the tracks a search happened to return.
  const completeAlbum = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/music/albums/${id}/complete`);
      toast.success(`+${data.added}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCompleting(false);
    }
  };

  const load = () =>
    api.get(`/music/albums/${id}`).then((r) => setData(r.data)).catch(() => setData(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (data === null) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (data === false) return <div className="p-10 text-zinc-400">{t("album_not_found")}</div>;

  return (
    <div>
      <div className="p-4 md:p-8 pb-6 bg-gradient-to-b from-blue-700/30 to-transparent flex items-end gap-6">
        {data.cover ? <img src={data.cover} alt="" className="w-40 h-40 rounded-lg object-cover shadow-2xl" /> : <div className="w-40 h-40 rounded-lg bg-white/10 flex items-center justify-center"><Disc3 className="w-16 h-16 text-zinc-500" /></div>}
        <div>
          <p className="text-xs uppercase tracking-wider font-bold">{t("album")}</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mt-2">{data.name}</h1>
          <p className="text-zinc-300 mt-3 text-sm">{data.artist} · {data.tracks.length} {t("tracks")}</p>
        </div>
      </div>
      <div className="px-4 md:px-8 py-4 flex items-center gap-3 flex-wrap">
        <button data-testid="album-play-all" onClick={() => data.tracks.length && playQueue(data.tracks, 0)} className="w-14 h-14 rounded-full bg-[#FF2A4D] hover:bg-[#E61C3D] hover:scale-105 flex items-center justify-center transition-all shadow-[0_0_20px_rgba(255,69,0,0.4)]">
          <Play className="w-6 h-6 text-white ml-1" fill="white" />
        </button>
        {isAdmin && (
          <button
            data-testid="album-complete"
            onClick={completeAlbum}
            disabled={completing}
            className="rounded-full px-5 py-2.5 text-sm font-semibold border border-white/20 text-white hover:border-white/50 transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
            {completing ? t("completing") : t("complete_album")}
          </button>
        )}
      </div>
      <div className="px-5 pb-6">
        {data.tracks.map((tr, i) => (
          <TrackRow key={tr.id} track={tr} index={i} tracks={data.tracks} />
        ))}
      </div>
    </div>
  );
}
